import {
  useGetQuery,
  useLazyGetQuery,
  usePostMutation,
  usePutMutation,
} from "slices/api";
import {
  FIRE_SAFETY_APPLICATION,
  FIRE_SAFETY_BUILDING_OBSERVATION,
} from "tagTypes";

export const useGetObserverTypeQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/observation-type",
    query,
    tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
  });

export const useGetBuildingSchedulesQuery = (buildingInformationId) =>
  useGetQuery({
    endpoint: `/inspection-observation/building-information/${buildingInformationId}`,
    tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
  });

export const useLazyFireSafetyBuildingObservationQuery = () => {
  const [getObservation, data] = useLazyGetQuery();
  const getBuildingObservationData = async (scheduleId, query = {}) =>
    await getObservation(
      {
        endpoint: `/inspection-observation/schedule/${scheduleId}`,
        query,
        tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
      },
      true
    ).unwrap();
  return [getBuildingObservationData, data];
};

export const useLazyGetFireSafetyObservationNoticeQuery = () => {
  const [getNoticeData, data] = useLazyGetQuery();
  const getObservationNotice = async (scheduleId, query = {}) =>
    await getNoticeData(
      {
        endpoint: `/inspection-observation/schedule/${scheduleId}/inspection-observation-notice-document`,
        query,
        tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
      },
      true
    ).unwrap();
  return [getObservationNotice, data];
};

export const usePostFireSafetyBuildingObservationMutation = () => {
  const [buildingObservation, data] = usePostMutation();
  const addBuildingObservation = async (payload, query = {}) =>
    await buildingObservation({
      endpoint: "/inspection-observation",
      query,
      payload: payload || {},
      toastMessage: "Building observation added successfully",
      tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
    }).unwrap();
  return [addBuildingObservation, data];
};

export const usePutSendNoticeMutation = () => {
  const [sendNotice, data] = usePutMutation();
  const sendNoticeData = async (scheduleId) =>
    await sendNotice({
      endpoint: `/inspection-observation/schedule/${scheduleId}/send-notice`,
      toastMessage: "Notice sent successfully",
      tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
    }).unwrap();
  return [sendNoticeData, data];
};

export const usePostFireSafetyResolvedObservationPhotographsMutation = () => {
  const [postResolvePhotographs, data] = usePostMutation();
  const addResolvePhotographs = async (observationId, payload, query = {}) =>
    await postResolvePhotographs({
      endpoint: `/inspection-observation/${observationId}/upload-inspection-observation-resolve-photograph`,
      query,
      payload: payload || {},
      toastMessage: "Resolved photograph added successfully",
      tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
    }).unwrap();
  return [addResolvePhotographs, data];
};

export const usePutCloseBuildingObservationByIdMutation = () => {
  const [closeInspectionObservation, data] = usePutMutation();
  const updateBuildingObservation = async (observationId, query = {}) =>
    await closeInspectionObservation({
      endpoint: `/inspection-observation/${observationId}/close-inspection-observation`,
      query,
      toastMessage: "Observation closed successfully",
      tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
    }).unwrap();
  return [updateBuildingObservation, data];
};

export const usePutGenerateObservationImagesByScheduleIdMutation = () => {
  const [closeInspectionObservation, data] = usePutMutation();
  const updateBuildingObservation = async (scheduleId, query = {}) =>
    await closeInspectionObservation({
      endpoint: `/inspection-observation/${scheduleId}/generate-comparison-photograph`,
      query,
      toastMessage: "Observation closed successfully",
      tags: [FIRE_SAFETY_BUILDING_OBSERVATION, FIRE_SAFETY_APPLICATION],
    }).unwrap();
  return [updateBuildingObservation, data];
};

export const useLazyGetNoticeUrlByScheduleId = () => {
  const [getNoticeUrl, data] = useLazyGetQuery();
  const getNoticeUrlData = async (scheduleId) =>
    await getNoticeUrl(
      {
        endpoint: `/inspection-observation/schedule/${scheduleId}/print-notice`,
      },
      true
    ).unwrap();
  return [getNoticeUrlData, data];
};